import { AfterViewInit, Component, Input, ViewChild } from '@angular/core';
import { AIChatbotMessage } from '../../models/ai-chatbot-message';
import { AIChatbotService } from '../../services/ai-chatbot/ai-chatbot.service';
import { AiChatbotPresentationData } from '../../models/ai-chatbot-presentation-data';
import { TemplateEditorService } from 'src/app/template-editor/services/template-editor.service';
import { BroadcasterService } from 'src/app/shared/services/broadcaster.service';
import { PromiseUtilsService } from 'src/app/shared/services/promise-utils.service';
import { PresentationPreviewComponent } from 'src/app/presentations/components/presentation-preview/presentation-preview.component';

@Component({
  selector: 'ai-chatbot-message',
  templateUrl: './ai-chatbot-message.component.html',
  styleUrls: ['./ai-chatbot-message.component.scss'],
  standalone: false,
})
export class AiChatbotMessageComponent implements AfterViewInit {
  @ViewChild('presentationPreview') public presentationPreview: PresentationPreviewComponent;

  @Input({ required: true }) message!: AIChatbotMessage;
  @Input() isLastMessage: boolean;

  constructor(
    public aiChatbotService: AIChatbotService,
    private templateEditorService: TemplateEditorService,
    private broadcaster: BroadcasterService,
    private promiseUtilsService: PromiseUtilsService
  ) {}

  ngAfterViewInit(): void {
    if (this.message.content?.type === 'single_presentation') {
      const content = this.message.content;

      this.createPresentationPreview(content.data)
        .then(() => this.promiseUtilsService.delay(500))
        .then(() => this.updateAttributeData(content.data.attributeData))
        .then(() => {
          this.aiChatbotService.presentationEdit$.subscribe(attributeData => {
            content.data.attributeData = attributeData;
            this.updateAttributeData(attributeData);
          });
        });
    }
  }

  updateAttributeData(attributeData) {
    this.presentationPreview.postAttributeData(attributeData);
  }

  onChipKeyDown(event: KeyboardEvent, message: string): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.aiChatbotService.isProcessing) {
        this.sendMessage(message);
      }
    }
  }

  onPresentationOptionKeyDown(
    event: KeyboardEvent,
    presentation: AiChatbotPresentationData
  ): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!this.aiChatbotService.isProcessing) {
        this.aiChatbotService.selectPresentation(presentation);
      }
    }
  }

  sendMessage(message: string): void {
    const userMessage: AIChatbotMessage = {
      id: this.aiChatbotService.generateMessageId(),
      timestamp: new Date(),
      role: 'user',
      content: {
        type: 'message',
        message,
      },
    };

    this.aiChatbotService.sendMessage(userMessage);
  }

  openPresentationInEditor() {
    if (this.message.content?.type === 'single_presentation') {
      return this.aiChatbotService.openPresentationInEditor(this.message.content.data);
    }
  }

  // Helper to avoid compilation/sum type issue in template
  getSearchedPresentations(): AiChatbotPresentationData[] {
    if (this.message.content?.type === 'presentations') {
      return this.message.content.data.templates;
    }

    return [];
  }

  async onExpandPresentation(presentation: AiChatbotPresentationData) {
    return this.aiChatbotService.selectPresentation(presentation);
  }

  createPresentationPreview(template) {
    return this.templateEditorService.addFromProduct({
      productCode: template.productCode,
      name: template.name + ' - ' + new Date().toISOString(),
    })
    .then(() => {
      this.broadcaster.emit('risevision.aiservice.presentationChanged');
    })
    .catch((err) => {
      console.error('Error creating template:', err);
    })
    .finally(() => {
      this.templateEditorService.aiEditorActive = false;
    });
  }
}
